import {
    ColumnDef,
    getReceiptColumns,
} from 'components/contracts-page-wrapper/contracts-page-wrapper-helper';
import { Chip } from '@biq/apex-ui';
import { TruncateChipList } from 'components';
import { toLocalDateTimeString } from 'lib';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';

export const tableHeight: number = 280;

export type TabsItemsType = 'Contracts' | 'Receipts';

export const ShareWithChipKeys = {
    ALL: 'all',
    USER: 'user',
    GROUP: 'group',
    JWT: 'jwt',
};

export const TabItems: TabItemsType[] = [
    { label: 'Contracts' },
    { label: 'Receipts' },
];

export const consentContractStyles = {
    chipStyles: {
        height: '24px',
    },
};

const getUserNamesByIds = (
    values: string[],
    userList: any[],
    userGroupList: any[]
): { user: string[]; group: string[] } => {
    const userNames: string[] = [];
    const groupNames: string[] = [];

    for (const id of values) {
        const user = userList.find((u) => u?.id === id);
        if (user) {
            userNames.push(user.fullName);
        } else {
            const groupUser = userGroupList.find((g) => g?.id === id);
            if (groupUser) {
                groupNames.push(groupUser.name);
            }
        }
    }

    return {
        user: userNames,
        group: groupNames,
    };
};

export const isInvalidDate = (date: any): boolean => {
    if (date === 'Invalid Date') {
        return true;
    }
    return Number.isNaN(date?.getTime());
};

export const getConsentContractStatus = (start: any, end: any): string => {
    const endDate = new Date(end);
    const startDate = new Date(start);
    const currentDate = new Date();
    let status = '';
    if (!isInvalidDate(endDate) && endDate < currentDate) {
        // Check if expired
        status = 'Expired';
    } else if (!isInvalidDate(startDate) && currentDate < startDate) {
        // Check if scheduled
        status = 'Scheduled';
    } else {
        // If active
        status = 'Active';
    }

    return status;
};

export const getConsentContractSharedWithColumnData = (
    sharedWithData: any[],
    userList: any[],
    userGroupList: any[]
): { [key: string]: string[] } => {
    if (!sharedWithData.length) {
        return { [ShareWithChipKeys.JWT]: ['JWT'] };
    }

    if (sharedWithData.includes('*')) {
        return { [ShareWithChipKeys.ALL]: ['ALL'] };
    }

    const nameObj = getUserNamesByIds(sharedWithData, userList, userGroupList);

    const result: { [key: string]: string[] } = {};

    if (nameObj.user?.length) {
        result[ShareWithChipKeys.USER] = nameObj.user.map((name) =>
            name.length > 25 ? name.slice(0, 25) + '...' : name
        );
    }

    if (nameObj.group?.length) {
        result[ShareWithChipKeys.GROUP] = nameObj.group.map((name) =>
            name.length > 25 ? name.slice(0, 25) + '...' : name
        );
    }
    return result;
};

export const consentContractStatus: {
    [key: string]: {
        color: 'primary' | 'secondary' | 'grayscale';
        label: string;
    };
} = {
    Scheduled: {
        color: 'secondary',
        label: 'Scheduled',
    },
    Active: {
        color: 'primary',
        label: 'Active',
    },
    Expired: {
        color: 'grayscale',
        label: 'Expired',
    },
};

export const getColumns = (
    selectedTab: TabsItemsType,
    userWallets: any[],
    systemWallets: any[]
): ColumnDef[] => {
    let columns = {
        Contracts: [
            {
                field: 'shareWith',
                headerName: 'Shared With',
                flex: 1.2,
                resizable: false,
                sortable: false,
                renderCell: (params: any): any => {
                    if (!params.value) return null;

                    const {
                        [ShareWithChipKeys.ALL]: all,
                        [ShareWithChipKeys.JWT]: jwt,
                        [ShareWithChipKeys.USER]: user,
                        [ShareWithChipKeys.GROUP]: group,
                    } = params.value;

                    if (all?.length) {
                        return (
                            <Chip
                                label={'ALL'}
                                variant='outlined'
                                color='secondary'
                                sx={consentContractStyles.chipStyles}
                                readOnly
                            />
                        );
                    }

                    if (jwt?.length) {
                        return (
                            <Chip
                                label={'JWT'}
                                variant='outlined'
                                color='primary'
                                sx={consentContractStyles.chipStyles}
                                readOnly
                            />
                        );
                    }

                    const combinedChips = [
                        ...(user?.map((item: string) => ({
                            label: item,
                            color: 'grayscale',
                        })) ?? []),
                        ...(group?.map((item: string) => ({
                            label: item,
                            color: 'secondary',
                        })) ?? []),
                    ];

                    if (!combinedChips.length) return null;

                    return (
                        <TruncateChipList
                            chipData={combinedChips}
                            chipVariant='outlined'
                            chipStyles={consentContractStyles.chipStyles}
                        />
                    );
                },
            },
            {
                field: 'timestamp',
                headerName: 'Last Modified',
                flex: 0.5,
                renderCell: (params: any): any => {
                    return toLocalDateTimeString(params.value, true);
                },
            },
            {
                field: 'start',
                headerName: 'Shared From',
                flex: 0.5,
                resizable: false,
                renderCell: (params: any): any => {
                    return toLocalDateTimeString(params.value);
                },
            },
            {
                field: 'until',
                headerName: 'Shared Until',
                flex: 0.5,
                resizable: false,
                renderCell: (params: any): any => {
                    return toLocalDateTimeString(params.value);
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 0.5,
                resizable: false,
                valueGetter: (params: any): any => params,
                renderCell: (params: any): any => {
                    const status = params?.row?.status;
                    return (
                        <Chip
                            color={consentContractStatus[status].color}
                            label={consentContractStatus[status].label}
                            size='small'
                            readOnly
                        />
                    );
                },
            },
        ],
        Receipts: getReceiptColumns(false, userWallets, systemWallets),
    };

    return columns[selectedTab];
};
